/** Группы таблиц на экране «Объёмы» (замечание инженера ПТО 23.10, п. 3: «группы
 *  как в ВК1», «нужна разбивка по группам»): путь группы строки — `raw.group`
 *  («Сваи буронабивные СБм1 / Материалы», его пишут разбор и проход «Обновить
 *  разделы»); строка-заголовок группы (`raw.group_heading`) строкой не
 *  выводится. Вид «По группам» в комплектах без систем — вложенные
 *  сворачиваемые уровни пути ▶ строки в порядке РД.
 *
 *  «Поз.» РД — в графе «Шифр» (ответ инженера на вопрос 10, вариант «б»: номер
 *  строки сквозной под заголовками групп). «Поз.» у строки таблицы — графа
 *  позиции РД (роль `code` по шапке «Поз.», `spec_tables/columns.rs`) или номер в
 *  начале наименования; склеенный OCR номер («11» вместо «1.1», НПС2 СП01)
 *  восстанавливается по порядку внутри группы, если это надёжно.
 *
 *  Без импортов значений (только `import type`): модуль проверяет `node --test`
 *  (`volumes/groups.test.mjs`), а Node не знает путей-алиасов. */
import type { PtoVolumePosition } from '@shared/api';

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Подпись строк без заголовка группы таблицы. */
export const NO_GROUP = 'без группы';
/** Разделитель уровней пути группы. */
export const GROUP_SEP = ' / ';

/** Строка-заголовок группы (проход «Обновить разделы»): строкой не выводится,
 *  номера не получает. */
export function isGroupHeading(raw: unknown): boolean {
  return asObj(raw)?.group_heading === true;
}

/** Путь группы строки: уровни `raw.group`; пусто — группы нет. */
export function groupPath(raw: unknown): string[] {
  const g = text(asObj(raw)?.group);
  return g ? g.split(GROUP_SEP).map((s) => s.trim()) : [];
}

/** Строки без строк-заголовков групп. */
export function withoutGroupHeadings<T extends Pick<PtoVolumePosition, 'raw'>>(
  items: readonly T[],
): T[] {
  return items.filter((p) => !isGroupHeading(p.raw));
}

/** В строках есть группы таблиц — графа «Группа» и вид «По группам». */
export function hasGroups(items: readonly Pick<PtoVolumePosition, 'raw'>[]): boolean {
  return items.some((p) => !isGroupHeading(p.raw) && groupPath(p.raw).length > 0);
}

// ── Вид «По группам» ──

/** Узел дерева групп: уровень пути, его строки и вложенные уровни. */
export interface GroupNode<T> {
  /** Путь до узла через « / »; у «без группы» — пусто. */
  key: string;
  /** Подпись уровня («Материалы») или «без группы». */
  label: string;
  depth: number;
  /** Строк в узле со всеми вложенными. */
  count: number;
  /** Строки этого уровня (без вложенных) — в порядке РД. */
  rows: T[];
  children: GroupNode<T>[];
}

/** Строки → дерево групп по пути `raw.group`: уровни — в порядке первой строки
 *  (порядок РД), «без группы» — последним; строки-заголовки не выводятся. */
export function groupTree<T extends Pick<PtoVolumePosition, 'id' | 'raw'>>(
  items: readonly T[],
): GroupNode<T>[] {
  const roots: GroupNode<T>[] = [];
  const byKey = new Map<string, GroupNode<T>>();
  let ungrouped: GroupNode<T> | null = null;
  for (const p of items) {
    if (isGroupHeading(p.raw)) continue;
    const path = groupPath(p.raw);
    if (path.length === 0) {
      ungrouped ??= { key: '', label: NO_GROUP, depth: 0, count: 0, rows: [], children: [] };
      ungrouped.rows.push(p);
      ungrouped.count += 1;
      continue;
    }
    let level = roots;
    let node: GroupNode<T> | null = null;
    for (let i = 0; i < path.length; i += 1) {
      const key = path.slice(0, i + 1).join(GROUP_SEP);
      let n = byKey.get(key);
      if (!n) {
        n = { key, label: path[i], depth: i, count: 0, rows: [], children: [] };
        byKey.set(key, n);
        level.push(n);
      }
      n.count += 1;
      node = n;
      level = n.children;
    }
    node?.rows.push(p);
  }
  if (ungrouped) roots.push(ungrouped);
  return roots;
}

/** Ключи всех узлов дерева (для «Развернуть все»). */
export function nodeKeys<T>(nodes: readonly GroupNode<T>[]): string[] {
  return nodes.flatMap((n) => [n.key, ...nodeKeys(n.children)]);
}

/** Ключи узлов по пути к строке `id` (для раскрытия строки из ссылки); пусто —
 *  строки в дереве нет. */
export function nodePathOf<T extends Pick<PtoVolumePosition, 'id'>>(
  nodes: readonly GroupNode<T>[],
  id: string,
): string[] {
  for (const n of nodes) {
    if (n.rows.some((r) => r.id === id)) return [n.key];
    const below = nodePathOf(n.children, id);
    if (below.length > 0) return [n.key, ...below];
  }
  return [];
}

// ── «Поз.» РД в графе «Шифр» ──

/** «Поз.» строки: прочитанная и, если восстановлена, — по порядку в группе. */
export interface RdPos {
  /** «Поз.» для графы «Шифр» («1.1», «5»). */
  pos: string;
  /** Как прочитано, если «Поз.» восстановлена («11»); иначе null. */
  read: string | null;
}

/** Номер «Поз.»: цифры через точку («5», «2.1», «3.10»), точка в конце — не в счёт. */
const POS_NUMBER = /^\d{1,3}(?:\.\d{1,3})*\.?$/;
/** Номер в начале наименования: «16. Труба …», «5.1.1 Установка …». */
const NAME_NUMBER = /^(\d{1,3}(?:\.\d{1,3})*)\.?\s+\S/;
/** Номер раздела группы: «3. Изделия и материалы» → «3». */
const GROUP_NUMBER = /^(\d{1,2})(?:[.,]\s*|\s+)/;

type PosItem = Pick<PtoVolumePosition, 'id' | 'raw' | 'code' | 'name' | 'source_role'>;

/** «Поз.» строки как прочитана: графа позиции с номером, иначе номер в начале
 *  наименования (графы позиции нет). Марка («КП-1», «Зд1») — не номер. */
export function readPos(p: Pick<PtoVolumePosition, 'code' | 'name'>): string | null {
  const code = p.code?.trim() ?? '';
  if (code) return POS_NUMBER.test(code) ? code.replace(/\.$/, '') : null;
  const m = NAME_NUMBER.exec(p.name.trim());
  return m ? m[1] : null;
}

/** Номер раздела группы строки (последний нумерованный уровень пути). */
function groupNumber(raw: unknown): string | null {
  const path = groupPath(raw);
  for (let i = path.length - 1; i >= 0; i -= 1) {
    const m = GROUP_NUMBER.exec(path[i]);
    if (m) return m[1];
  }
  return null;
}

/** «G.k» → k, если номер — этого раздела. */
function subOf(pos: string | null | undefined, g: string): number | null {
  if (!pos) return null;
  const m = /^(\d{1,2})\.(\d{1,3})$/.exec(pos);
  return m && m[1] === g ? Number(m[2]) : null;
}

/** «Поз.» строк таблиц (строки — в порядке РД). Под нумерованным разделом
 *  («3. Изделия и материалы»), где «Поз.» вида «3.k», номер, который OCR склеил
 *  («34» вместо «3.4») или исказил между двумя верными соседями («3.1» между
 *  «3.6» и «3.8»), — восстановлен по порядку; прочее — как прочитано. */
export function rdPositions(items: readonly PosItem[]): Map<string, RdPos> {
  const out = new Map<string, RdPos>();
  // Строки по группам (путь целиком) — в порядке РД.
  const groups = new Map<string, PosItem[]>();
  for (const p of items) {
    if (p.source_role !== 'rd_table' || isGroupHeading(p.raw)) continue;
    const read = readPos(p);
    if (!read) continue;
    out.set(p.id, { pos: read, read: null });
    const key = groupPath(p.raw).join(GROUP_SEP);
    const list = groups.get(key) ?? [];
    list.push(p);
    groups.set(key, list);
  }
  // Нумерация «G.k» в разделах — хотя бы у одной строки (иначе сквозная «Поз.»,
  // как в КЖ, не трогается).
  const dotted = [...groups.values()].some((rows) => {
    const g = groupNumber(rows[0].raw);
    return g !== null && rows.some((p) => subOf(out.get(p.id)?.pos, g) !== null);
  });
  for (const rows of dotted ? groups.values() : []) {
    const g = groupNumber(rows[0].raw);
    if (!g) continue;
    const reads = rows.map((p) => out.get(p.id)?.pos ?? '');
    let prev = 0;
    rows.forEach((p, i) => {
      const read = reads[i];
      const want = prev + 1;
      const expected = `${g}.${want}`;
      let pos = read;
      if (read === `${g}${want}`) {
        pos = expected; // склеено: «34» = «3.4»
      } else if (subOf(read, g) !== want) {
        const next = subOf(reads[i + 1], g);
        if (prev > 0 && next === want + 1) pos = expected; // между верными соседями
      }
      if (pos !== read) out.set(p.id, { pos, read });
      prev = subOf(pos, g) ?? prev;
    });
  }
  return out;
}
